import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  autoTranslateChapter,
  configureSync,
  createTranslationProject,
  fetchTranslationProject,
  fetchTranslationProjects,
  importFromGithub,
  ProjectDocs,
  regenerateAudio,
  resolveSegmentConflict,
  saveReadingPosition,
  setAutoTranslate,
  suggestWording,
  synthesizeAudio,
  syncToGithub,
  translateChapter,
  updateProjectDocs,
  updateSegment,
  uploadEpub,
} from '../lib/translation/api';

export function useTranslationProjects() {
  return useQuery({ queryKey: ['translationProjects'], queryFn: fetchTranslationProjects });
}

export function useTranslationProject(id: string | undefined, opts: { poll?: boolean } = {}) {
  return useQuery({
    queryKey: ['translationProject', id],
    queryFn: () => fetchTranslationProject(id as string),
    enabled: !!id,
    // Poll while ingesting, while a chapter is being autotranslated, or when the
    // caller asks (e.g. waiting on a prefetch), so new text appears without reload.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.status === 'ingesting') return 2000;
      if (opts.poll || data?.chapters?.some((c) => c.translating)) return 4000;
      return false;
    },
  });
}

export function useCreateTranslationProject() {
  return useMutation({ mutationFn: createTranslationProject });
}

export function useImportFromGithub() {
  return useMutation({ mutationFn: importFromGithub });
}

export function useUploadEpub() {
  return useMutation({
    mutationFn: ({ file, targetLanguage }: { file: File; targetLanguage: string }) =>
      uploadEpub(file, targetLanguage),
  });
}

export function useUpdateProjectDocs(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (docs: ProjectDocs) => updateProjectDocs(projectId as string, docs),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['translationProject', projectId] }),
  });
}

export function useRegenerateAudio(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => regenerateAudio(projectId as string),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['translationProject', projectId] }),
  });
}

export function useConfigureSync(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (repoName?: string) => configureSync(projectId as string, repoName),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['translationProject', projectId] }),
  });
}

export function useTranslateChapter(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: translateChapter,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['translationProject', projectId] }),
  });
}

export function useSetAutoTranslate(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => setAutoTranslate(projectId as string, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['translationProject', projectId] }),
  });
}

export function useAutoTranslateChapter(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: autoTranslateChapter,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['translationProject', projectId] }),
  });
}

export function useUpdateSegment(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ segmentId, targetText }: { segmentId: string; targetText: string }) =>
      updateSegment(segmentId, targetText),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['translationProject', projectId] }),
  });
}

export function useResolveSegmentConflict(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { segmentId: string; choice: 'mine' | 'repo' | 'merge'; mergedText?: string }) =>
      resolveSegmentConflict(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['translationProject', projectId] }),
  });
}

export function useSuggestWording() {
  return useMutation({ mutationFn: suggestWording });
}

export function useSynthesizeAudio(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: synthesizeAudio,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['translationProject', projectId] }),
  });
}

export function useSyncToGithub(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => syncToGithub(projectId as string),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['translationProject', projectId] }),
  });
}

export function useSaveReadingPosition() {
  return useMutation({ mutationFn: saveReadingPosition });
}
